import _ from 'lodash';
import classNames from 'classnames';

import React, { useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Grid,
  Tooltip,
  TextField,
  IconButton,
  Typography,
  Divider,
} from '@material-ui/core';
import ChevronLeftRoundedIcon from '@material-ui/icons/ChevronLeftRounded';
import SaveRoundedIcon from '@material-ui/icons/SaveRounded';
import CheckBoxOutlinedIcon from '@material-ui/icons/CheckBoxOutlined';
import CheckBoxOutlineBlankOutlinedIcon from '@material-ui/icons/CheckBoxOutlineBlankOutlined';
import { Listing, Shooting } from 'models';
import { ShootingContext } from 'contexts';
import { useAuth, useLanguage } from 'hooks';
import { IAmenity, InputChangeEvent } from 'types';
import { images } from 'assets/images';
import InfoRoundedIcon from '@material-ui/icons/InfoRounded';
import {
  CONDO_MIN_ROOMS,
  ENG,
  HOUSE_MIN_ROOMS,
  MAX_ROOMS,
  SHOOTING_STEPS,
  COLOR,
} from 'config/constants';

const useStyles = makeStyles({
  stepContent: {
    '& .MuiIconButton-root': {
      padding: 0,
    },

    '& .MuiSvgIcon-root': {
      fontSize: 35,
    },
  },
  sizeContainer: {
    fontSize: '1rem',
    textTransform: 'uppercase',

    '& p': {
      fontWeight: 600,
    },
  },
  roomSizeControl: {
    display: 'flex',
    alignItems: 'center',
  },
  roomSizeValue: {
    width: 50,
    textAlign: 'center',
  },
  sliderThumb: {
    width: 20,
    height: 20,
    boxShadow: 'none',
    marginTop: -10,

    '&:hover': {
      boxShadow: 'none',
    },

    '&.MuiSlider-active': {
      boxShadow: 'none',
    },
  },
  amenities: {
    width: '100%',

    '& button': {
      width: '100%',
      fontSize: 12,
      fontWeight: 600,
      marginRight: 5,
      marginTop: 5,
    },
  },
  amenityButton: {
    height: 60,
    borderRadius: 8,
    '&.MuiButton-containedSecondary': {
      color: COLOR.WHITE,
    },
    '@media (max-width: 767px)': {
      height: 40,
    },
  },
  saveButton: {
    color: COLOR.WHITE,
    '@media (max-width: 767px)': {
      fontSize: 10,
    },
  },
  buttons: {
    '@media (max-width: 767px)': {
      display: 'flex',
      flexDirection: 'row',
    },
  },
  nextButton: {
    color: COLOR.WHITE,
    '@media (max-width: 767px)': {
      fontSize: 10,
    },
  },
  backButton: {
    backgroundColor: COLOR.WHITE,
    color: COLOR.LIGHT_BLUE,
    borderColor: COLOR.LIGHT_BLUE,
    marginRight: 20,
    '@media (max-width: 767px)': {
      fontSize: 10,
      marginRight: 5,
    },
  },
  row: {
    display: 'flex',
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    '@media (max-width: 767px)': {
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
    },
    fontSize: '1rem',
    textTransform: 'uppercase',

    '& p': {
      fontWeight: 600,
    },
  },
  roomCountControl: {
    cursor: 'pointer',
    height: 120,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    backgroundColor: COLOR.WHITE,
    outline: `1px solid ${COLOR.SYSTEM_GREY}`,
    zIndex: 1,
    flexDirection: 'column',
    '@media (max-width: 767px)': {
      height: 100,
    },
  },
  selectedRoomCountControl: {
    cursor: 'pointer',
    height: 120,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    backgroundColor: COLOR.LIGHT_BLUE,
    color: COLOR.WHITE,
    outline: `1.5px solid ${COLOR.SYSTEM_GREY}`,
    zIndex: 1,
    flexDirection: 'column',
    '@media (max-width: 767px)': {
      height: 100,
    },
  },
  propertyIcon: {
    width: 25,
    height: 25,
    '@media (max-width: 767px)': {
      width: 20,
      height: 20,
    },
  },
  propertyType: {
    fontFamily: 'FordAntenna-Medium',
    marginTop: 5,
  },
  sizeText: {
    marginTop: 5,
    textTransform: 'lowercase',
    '@media (max-width: 767px)': {
      fontSize: 12,
    },
  },
  iconContainer: {
    height: 30,
  },
  divider: {
    marginTop: 20,
    marginBottom: 20,
  },
  iconRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  infoIcon: {
    width: 25,
    height: 25,
    color: COLOR.LIGHT_BLUE,
  },
  optionsBox: {
    display: 'flex',
    flexDirection: 'column',
  },
  optionBox: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: 10,
    cursor: 'pointer',
  },
  block: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    marginBottom: 15,
  },
  checkBox: {
    marginRight: 10,
  },
  amenityIcon: {
    marginRight: 10,
  },
  amenityText: {
    textTransform: 'capitalize',
  },
  buttonText: {
    '@media (max-width: 767px)': {
      display: 'none',
    },
  },
});

interface SliderTooltipProps {
  children: any;
  open: boolean;
  value: number;
}
interface Props {
  setActiveStep: (_: number) => void;
  onCancel: () => void;
}

function PropertyStep({ setActiveStep, onCancel }: Props) {
  const intl = useIntl();
  const styles = useStyles();
  const language = useLanguage();

  const { user } = useAuth();
  const {
    listing,
    shooting,
    amenities,
    isEditMode,
    isPropertySizeConfirmed,
    updateListingFields,
    updateShootingFields,
    saveShooting,
    setPropertySizeConfirmed,
    validatePropertyStep,
    shouldChooseTimeslot,
  } = useContext(ShootingContext);
  const isDraftMode = shooting.state === Shooting.STATE_ENUM.draft;

  const { shooting_params } = shooting;
  const roomCount = shooting_params?.room_count;
  const shootingAmenities = shooting_params?.getAmenities() || [];

  const fmtIgnoreEmail = intl.formatMessage({ id: 'shooting.create.labels.ignore_email' });
  const fmtSaveDraft = intl.formatMessage({ id: 'shooting.create.buttons.saveDraft' });
  const fmtSave = intl.formatMessage({ id: 'shooting.create.buttons.save' });
  const fmtPropertySize = intl.formatMessage({ id: 'shooting.create.labels.propertySize' });
  const fmtSizeUnit = intl.formatMessage({ id: 'deliver.measures.squareFoot' });
  const fmtAccessMethod = intl.formatMessage({ id: 'shooting.create.labels.accessMethod' });
  const fmtAccessMethodTooltip = intl.formatMessage({
    id: 'shooting.create.labels.enterPropertyInstructions',
  });
  const fmtKeyBoxPlaceholder = intl.formatMessage({
    id: 'shooting.create.labels.keyBoxPlaceholder',
  });

  const isAmenitySelected = (name: string) => {
    const _amenity = _.find(shootingAmenities, (m: IAmenity) => {
      return m.name === name;
    });
    return !!_amenity?.selected;
  };

  const getPropertySize = (propertyType) => {
    const foundProperty = Listing.ROOM_COUNT_OPTIONS[listing.property_type].find((option) => {
      return option === propertyType;
    });
    return foundProperty ? foundProperty.count + 5 : 10;
  };

  const getPropertyType = (propertyType) => {
    const foundProperty = Listing.ROOM_COUNT_OPTIONS[listing.property_type].find((option) => {
      return option === propertyType;
    });
    return foundProperty ? foundProperty.name : '';
  };

  const getRoomCount = () => {
    if (shooting.id && shooting.state !== Shooting.STATE_ENUM.draft) {
      const roomCountOptions = Listing.ROOM_COUNT_OPTIONS[listing.property_type];
      const closestOption = roomCountOptions.reduce((prev, curr) => {
        return Math.abs(curr.count - roomCount) < Math.abs(prev.count - roomCount) ? curr : prev;
      });
      return closestOption.count;
    }
    return roomCount;
  };

  const onChangeRoomCount = (value: number) => {
    if (shooting.id && shooting.state !== Shooting.STATE_ENUM.draft) {
      return;
    }
    updateShootingFields({
      shooting_params: { room_count: value },
    });
  };

  const canAddRoom = () => {
    if (isEditMode && shooting.state !== Shooting.STATE_ENUM.draft) return false;
    return roomCount < MAX_ROOMS;
  };

  const canRemoveRoom = () => {
    if (isEditMode && shooting.state !== Shooting.STATE_ENUM.draft) return false;
    return listing.property_type === Listing.TYPE_ENUM.CONDO
      ? roomCount > CONDO_MIN_ROOMS
      : roomCount > HOUSE_MIN_ROOMS;
  };

  const onRemoveRoom = () => {
    if (!canRemoveRoom()) return;

    updateShootingFields({
      shooting_params: { room_count: roomCount - 1 },
    });
  };

  const onAddRoom = () => {
    if (!canAddRoom()) return;

    updateShootingFields({
      shooting_params: { room_count: roomCount + 1 },
    });
  };

  const onSquareFootageChange = (_: any, value: number) => {
    updateShootingFields({ square_footage: value });
  };

  const onPropertySizeConfirmChange = () => {
    setPropertySizeConfirmed(!isPropertySizeConfirmed);
  };

  const onAmenityChange = (_amenity: IAmenity) => {
    let _amenities = _.clone(shootingAmenities);
    const index = _.findIndex(_amenities, (m: IAmenity) => {
      return m.name === _amenity.name;
    });
    if (index !== -1) {
      _amenities[index].selected = !_amenities[index].selected;
    } else {
      _amenities.push({ ..._amenity, selected: true });
    }
    _amenities = _amenities.filter((m: IAmenity) => {
      return m.selected;
    });

    updateShootingFields({
      shooting_params: {
        amenities: JSON.stringify(_amenities),
      },
    });
  };

  const onMlsChange = (e: InputChangeEvent) => {
    updateListingFields({ mls: e.target.value });
  };

  const onIgnoreSchedulerConfirmChange = () => {
    updateShootingFields({ ignore_scheduler: !shooting.ignore_scheduler });
  };

  const onIgnoreEmailChange = () => {
    updateShootingFields({ ignore_email: !shooting.ignore_email });
  };

  const onClientPresentChange = () => {
    updateShootingFields({ client_present: !shooting.client_present });
  };

  const onBrokerPresentChange = () => {
    updateShootingFields({ broker_present: !shooting.broker_present });
  };

  const onKeyBoxNumberChange = (e: InputChangeEvent) => {
    updateShootingFields({ key_box_number: e.target.value });
  };

  const onPreviousStep = () => {
    setActiveStep(SHOOTING_STEPS.LOCATION);
  };

  const onUpdate = () => {
    if (!validatePropertyStep()) return;
    if (shooting.state !== Shooting.STATE_ENUM.draft && shouldChooseTimeslot()) {
      setActiveStep(SHOOTING_STEPS.SELECT_DATETIME);
    }
    saveShooting();
  };

  const onNextStep = () => {
    if (validatePropertyStep()) {
      setActiveStep(SHOOTING_STEPS.PACKAGE);
    }
  };

  const getRoomBgColor = (count: number, isDisabled: boolean) => {
    if (isDisabled && getRoomCount() !== count) {
      return COLOR.LIGHT_GREY;
    }
    if (getRoomCount() === count) {
      return COLOR.LIGHT_BLUE;
    }
    return COLOR.WHITE;
  };

  const getAmenityBgColor = (name: string) => {
    if (isAmenitySelected(name)) {
      return COLOR.LIGHT_BLUE;
    }
    if (isEditMode && shooting.state !== Shooting.STATE_ENUM.draft) {
      return COLOR.LIGHT_GREY;
    }
    return COLOR.WHITE;
  };
  const getAmenityIcon = (name: string) => {
    if (isAmenitySelected(name)) {
      return images.amenities[`${name.toLocaleLowerCase()}_white`];
    }
    return images.amenities[name.toLocaleLowerCase()];
  };

  return (
    <>
      <Box className={classNames('ScrollView', styles.stepContent)}>
        <h2 className={styles.title}>
          {intl.formatMessage({ id: 'shooting.create.titles.property' })}
        </h2>
        <Grid container spacing={1} className={styles.block}>
          <Grid item md={4}>
            <Typography className='InfoItem_Label'>{`${fmtPropertySize} *`}</Typography>
          </Grid>
          <Grid item md={8} sm={12} xs={12}>
            {Listing.ROOM_COUNT_OPTIONS[listing.property_type].length > 0 && (
              <Grid container spacing={2}>
                {Listing.ROOM_COUNT_OPTIONS[listing.property_type].map(
                  (option: any, index: number) => {
                    const isDisabled =
                      isEditMode &&
                      shooting.state !== Shooting.STATE_ENUM.draft &&
                      shooting.id !== undefined;
                    return (
                      <Grid
                        item
                        md={3}
                        sm={6}
                        xs={6}
                        onClick={() => {
                          return onChangeRoomCount(option.count);
                        }}
                      >
                        <div
                          className={
                            getRoomCount() === option.count
                              ? styles.selectedRoomCountControl
                              : styles.roomCountControl
                          }
                          style={{
                            backgroundColor: getRoomBgColor(option.count, isDisabled),
                            cursor: isDisabled ? 'default' : 'pointer',
                            outlineColor:
                              getRoomCount() !== option.count
                                ? COLOR.SYSTEM_GREY
                                : COLOR.LIGHT_BLUE,
                          }}
                        >
                          <div className={styles.iconContainer}>
                            <img
                              className={styles.propertyIcon}
                              src={
                                getRoomCount() === option.count
                                  ? images[`${getPropertyType(option)}_white`]
                                  : images[getPropertyType(option)]
                              }
                              style={{
                                height: getPropertySize(option),
                                width: getPropertySize(option),
                              }}
                              alt={getPropertyType(option)}
                            />
                          </div>
                          <Typography className={styles.propertyType}>
                            {intl.formatMessage({
                              id: `shooting.create.labels.${getPropertyType(option)}`,
                            })}
                          </Typography>
                          <Typography className={styles.sizeText}>
                            {`${option.size} ${fmtSizeUnit}`}
                          </Typography>
                        </div>
                      </Grid>
                    );
                  },
                )}
              </Grid>
            )}
          </Grid>
        </Grid>
        <Divider className={styles.divider} />
        <Grid container spacing={1} className={styles.block}>
          <Grid item md={4} className={styles.iconRow}>
            <Typography className='InfoItem_Label' style={{ marginRight: 0 }}>
              {`${fmtAccessMethod} *`}
            </Typography>
            <Tooltip title={fmtAccessMethodTooltip}>
              <InfoRoundedIcon className={styles.infoIcon} />
            </Tooltip>
          </Grid>
          <Grid item md={8} sm={12} className={styles.optionsBox}>
            <div className={styles.optionBox} onClick={onBrokerPresentChange}>
              <IconButton className={styles.checkBox}>
                {shooting.broker_present ? (
                  <CheckBoxOutlinedIcon color='secondary' />
                ) : (
                  <CheckBoxOutlineBlankOutlinedIcon color='secondary' />
                )}
              </IconButton>
              <Typography>
                {intl.formatMessage({ id: 'shooting.create.labels.brokerPresent' })}
              </Typography>
            </div>
            <div className={styles.optionBox} onClick={onClientPresentChange}>
              <IconButton className={styles.checkBox}>
                {shooting.client_present ? (
                  <CheckBoxOutlinedIcon color='secondary' />
                ) : (
                  <CheckBoxOutlineBlankOutlinedIcon color='secondary' />
                )}
              </IconButton>
              <Typography>
                {intl.formatMessage({ id: 'shooting.create.labels.clientPresent' })}
              </Typography>
            </div>
          </Grid>
        </Grid>
        <Grid container spacing={1} className={styles.block}>
          <Grid item md={4}>
            <Typography className='InfoItem_Label'>
              {intl.formatMessage({ id: 'shooting.create.labels.keyBox' })}
            </Typography>
          </Grid>
          <Grid item md={8} sm={12} xs={12}>
            <TextField
              variant='outlined'
              value={shooting.key_box_number}
              onChange={onKeyBoxNumberChange}
              fullWidth
              placeholder={fmtKeyBoxPlaceholder}
            />
          </Grid>
        </Grid>
        <Divider className={styles.divider} />
        {amenities.length ? (
          <Grid container spacing={1} className={styles.block}>
            <Grid item md={4}>
              <Typography className='InfoItem_Label'>
                {intl.formatMessage({ id: 'shooting.create.labels.commonAreas' })}
              </Typography>
            </Grid>
            <Grid item md={8} className={styles.amenities}>
              <Grid container spacing={2}>
                {amenities.map((m: any, index: number) => {
                  return (
                    <Grid item md={3} sm={6} xs={6}>
                      <Button
                        key={`${m.name}_${index}`}
                        variant='outlined'
                        className={styles.amenityButton}
                        style={{
                          backgroundColor: getAmenityBgColor(m.name),
                          color: isAmenitySelected(m.name) ? COLOR.WHITE : null,
                          borderColor: isAmenitySelected(m.name)
                            ? COLOR.LIGHT_BLUE
                            : COLOR.SYSTEM_GREY,
                        }}
                        onClick={() => {
                          if (isEditMode && shooting.state !== Shooting.STATE_ENUM.draft) {
                            return;
                          }
                          onAmenityChange(m);
                        }}
                      >
                        <img
                          src={getAmenityIcon(m.name)}
                          alt={m.name}
                          className={styles.amenityIcon}
                        />
                        <Typography className={styles.amenityText}>
                          {language === ENG ? m.name : m.name_fr}
                        </Typography>
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
            <Divider className={styles.divider} />
          </Grid>
        ) : null}

        {user.isAdmin ? (
          <Grid container spacing={1} className={styles.block}>
            <Grid item md={4}>
              <Typography className='InfoItem_Label'>
                {intl.formatMessage({ id: 'shooting.create.labels.ignore_scheduler_instructions' })}
              </Typography>
            </Grid>
            <Grid item md={8} sm={12} className={styles.optionsBox}>
              <div className={styles.optionBox} onClick={onIgnoreSchedulerConfirmChange}>
                <IconButton className={styles.checkBox}>
                  {shooting.ignore_scheduler ? (
                    <CheckBoxOutlinedIcon color='secondary' />
                  ) : (
                    <CheckBoxOutlineBlankOutlinedIcon color='secondary' />
                  )}
                </IconButton>
                <Typography>
                  {intl.formatMessage({ id: 'shooting.create.labels.ignore_scheduler' })}
                </Typography>
              </div>
              <div className={styles.optionBox} onClick={onIgnoreEmailChange}>
                <IconButton className={styles.checkBox}>
                  {shooting.ignore_email ? (
                    <CheckBoxOutlinedIcon color='secondary' />
                  ) : (
                    <CheckBoxOutlineBlankOutlinedIcon color='secondary' />
                  )}
                </IconButton>
                <Typography>{fmtIgnoreEmail}</Typography>
              </div>
            </Grid>
          </Grid>
        ) : null}
      </Box>

      <Box className='ButtonsBox'>
        <div className={styles.buttons}>
          <Button variant='contained' className='BackButton' onClick={onCancel}>
            {intl.formatMessage({ id: 'shooting.create.buttons.cancel' })}
          </Button>
          <Button
            className={styles.saveButton}
            variant='contained'
            color='secondary'
            onClick={onUpdate}
          >
            <SaveRoundedIcon />
            <Typography className={styles.buttonText}>
              {isDraftMode || !shooting.id ? fmtSaveDraft : fmtSave}
            </Typography>
          </Button>
        </div>
        <div className={styles.buttons}>
          <Button className={styles.backButton} variant='outlined' onClick={onPreviousStep}>
            <ChevronLeftRoundedIcon />
            {intl.formatMessage({ id: 'shooting.create.buttons.back' })}
          </Button>

          <Button
            variant='contained'
            className={styles.nextButton}
            color='secondary'
            onClick={onNextStep}
          >
            {`${intl.formatMessage({ id: 'shooting.create.buttons.next' })} (2/5)`}
          </Button>
        </div>
      </Box>
    </>
  );
}

export default PropertyStep;
